import React, { type FC, useMemo } from 'react';
import { connect, type MapStateToProps } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import HttpStatus from 'app/components/HttpStatus';
import { SsrModule } from 'app/constants/ssrModules';
import { withinBettingContext } from 'app/contexts/betting';
import type { SportId } from 'app/lib/betting/constants';
import { GridHeader, MatchesContainer } from 'app/modules/Betting';
import { MatchRows } from 'app/modules/Betting/components/MatchTable/components/MatchRows';
import { SubheaderBreadcrumbs } from 'app/modules/Betting/components/MatchTable/components/SubheaderBreadcrumbs';
import { Virtualizer } from 'app/modules/Prematch/components/Sport/Unions/Virtualizer';
import { useSSR } from 'app/packages/components/SkipSSR';
import type { StageWithHierarchy } from 'app/redux/betting-presentation/grid/selectors/allSportsGrid';
import {
  getFilteredTournamentsByTimeFilter,
} from 'app/redux/betting-presentation/grid/utils/get-filtered-tournaments-by-time-filter';
import { timeFilterValueProcessing } from 'app/redux/betting-presentation/grid/utils/time-filter-value-processing';
import EmptyGame from 'app/widgets/EmptyGame';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BettingContextProps = any;

type UnionsProps = {
  sportId: SportId;
  timeFilterValue?: string;
};

type StoreProps = {
  stagesSortedByGroupedTournamentsBySportId: Map<number, StageWithHierarchy>;
};


const UnionsWithoutHOC: FC<UnionsProps & StoreProps> = ({
  sportId,
  stagesSortedByGroupedTournamentsBySportId,
}) => {
  const isSSR = useSSR();

  const stageIdsSortedByTournament = useMemo(
    () => stagesSortedByGroupedTournamentsBySportId && (
      Array.from(stagesSortedByGroupedTournamentsBySportId.keys())
    ), [stagesSortedByGroupedTournamentsBySportId]);

  if (stagesSortedByGroupedTournamentsBySportId.size === 0) {
    return (
      <HttpStatus code={404}>
        <EmptyGame typeText="noFilteredEvents" />
      </HttpStatus>
    );
  }

  return (
    <div data-ssr-id={SsrModule.PREMATCH_UNIONS_TABLE}>
      <GridHeader shouldBeLink
                  sportId={sportId} />
      {isSSR
        ? (
          <MatchesContainer>
            {stageIdsSortedByTournament.map((stageIdSortedByTournament) => {
              const matchIds = stagesSortedByGroupedTournamentsBySportId
                .get(stageIdSortedByTournament)?.matchIds;
              if (!matchIds || matchIds.length === 0) {
                return null;
              }

              return (
                <li key={stageIdSortedByTournament}>
                  <SubheaderBreadcrumbs matchIds={matchIds}
                                        sportId={sportId} />
                  <MatchRows matchIds={matchIds} />
                </li>
              );
            })}
          </MatchesContainer>
        )
        : (
          <Virtualizer sportId={sportId}
                       tournamentIds={stageIdsSortedByTournament}
                       groupedTournamentsBySportId={stagesSortedByGroupedTournamentsBySportId} />
        )
      }
    </div>
  );
};

const mapStateToProps: MapStateToProps<
  StoreProps,
  BettingContextProps,
  ReduxState
> = (state, {
  $betting: {
    grid: {
      selectors: {
        stagesSortedByGroupedTournamentsBySportIdSelector,
        selectFilteredMatchesByTimeFilter,
      },
    },
  },
  sportId,
  timeFilterValue,
}) => {
  const stagesSortedByGroupedTournamentsBySportId = (
    stagesSortedByGroupedTournamentsBySportIdSelector(state, sportId)
  );

  if (!timeFilterValue || timeFilterValue === 'all') {
    return {
      stagesSortedByGroupedTournamentsBySportId,
    };
  }

  const {
    timeFilterValueNumber,
    isValidTimeFilterValue,
  } = timeFilterValueProcessing(timeFilterValue);

  if (!isValidTimeFilterValue) {
    return {
      stagesSortedByGroupedTournamentsBySportId,
    };
  }

  const filteredMatches = selectFilteredMatchesByTimeFilter(state, timeFilterValueNumber);

  const filtersGroupedTournamentsBySportId = getFilteredTournamentsByTimeFilter({
    filteredMatches,
    stages: stagesSortedByGroupedTournamentsBySportId,
    timeFilterValue,
  });

  return {
    stagesSortedByGroupedTournamentsBySportId: filtersGroupedTournamentsBySportId,
  };
};

export const UnionsVirtualizer = compose<FC<UnionsProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(UnionsWithoutHOC);
